import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './KeyRequestFormConfig.css';

const KeyRequestFormConfig = ({ gameId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [existingForm, setExistingForm] = useState(null);
  const [formData, setFormData] = useState({
    isActive: true,
    requirements: {
      minFollowers: 50,
      minAccountAge: 30,
      requiresVerifiedSocial: true,
      requiresTwitchOrYouTube: true,
      maxActiveRequests: 100
    },
    instructions: 'Please provide detailed information about your channel and content.',
    customQuestions: []
  });
  const [customQuestion, setCustomQuestion] = useState({ question: '', required: false });

  useEffect(() => {
    fetchExistingForm();
  }, [gameId]);

  const fetchExistingForm = async () => {
    try {
      const response = await axios.get(`/api/key-requests/form/${gameId}`);
      if (response.data.form) {
        setExistingForm(response.data.form);
        setFormData({
          isActive: response.data.form.isActive,
          requirements: response.data.form.requirements,
          instructions: response.data.form.instructions || '',
          customQuestions: response.data.form.customQuestions || []
        });
      }
    } catch (error) {
      // Form doesn't exist yet, that's fine
      console.log('No existing form found');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('requirements.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        requirements: {
          ...prev.requirements,
          [field]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const addCustomQuestion = () => {
    if (!customQuestion.question.trim()) {
      alert('Please enter a question');
      return;
    }

    if (formData.customQuestions.length >= 3) {
      alert('Maximum 3 custom questions allowed');
      return;
    }

    setFormData(prev => ({
      ...prev,
      customQuestions: [...prev.customQuestions, customQuestion]
    }));

    setCustomQuestion({ question: '', required: false });
  };

  const removeCustomQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      customQuestions: prev.customQuestions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/key-requests/form', {
        indieGameId: gameId,
        ...formData
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert('Key request form configured successfully!');
      if (onClose) onClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to configure form');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this key request form? This will prevent creators from submitting new requests.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/key-requests/form/${gameId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert('Key request form deleted successfully');
      if (onClose) onClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete form');
    }
  };

  return (
    <div className="key-request-config-modal">
      <div className="key-request-config-content">
        <div className="key-request-config-header">
          <h2>{existingForm ? 'Update' : 'Enable'} Key Request Form</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="key-request-config-form">
          <div className="config-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
              />
              <span>Enable Key Requests (creators can submit requests)</span>
            </label>
          </div>

          <div className="config-section">
            <h3>Creator Requirements</h3>
            
            <div className="form-group">
              <label>Minimum Total Followers/Subscribers</label>
              <input
                type="number"
                name="requirements.minFollowers"
                value={formData.requirements.minFollowers}
                onChange={handleInputChange}
                min="0"
                max="10000"
              />
              <small>Combined Twitch followers + YouTube subscribers</small>
              <small style={{ display: 'block', color: '#ffa500', marginTop: '5px' }}>
                💡 The website is new so not recommended to keep this high
              </small>
            </div>

            <div className="form-group">
              <label>Minimum Account Age (days)</label>
              <input
                type="number"
                name="requirements.minAccountAge"
                value={formData.requirements.minAccountAge}
                onChange={handleInputChange}
                min="0"
                max="365"
              />
              <small>How old their GamersUnion account must be</small>
            </div>

            <div className="form-group">
              <label>Maximum Pending Requests</label>
              <input
                type="number"
                name="requirements.maxActiveRequests"
                value={formData.requirements.maxActiveRequests}
                onChange={handleInputChange}
                min="1"
                max="1000"
              />
              <small>Limit how many pending requests you'll accept</small>
            </div>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="requirements.requiresVerifiedSocial"
                checked={formData.requirements.requiresVerifiedSocial}
                onChange={handleInputChange}
              />
              <span>Require at least one verified social account</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="requirements.requiresTwitchOrYouTube"
                checked={formData.requirements.requiresTwitchOrYouTube}
                onChange={handleInputChange}
              />
              <span>Require Twitch or YouTube channel</span>
            </label>
          </div>

          <div className="config-section">
            <h3>Instructions for Creators</h3>
            <textarea
              name="instructions"
              value={formData.instructions}
              onChange={handleInputChange}
              maxLength="1000"
              rows="4"
              placeholder="Add any special instructions or requirements..."
            />
            <small>{formData.instructions.length}/1000 characters</small>
          </div>

          <div className="config-section">
            <h3>Custom Questions (Optional)</h3>
            <small>Add up to 3 custom questions for creators</small>
            
            {formData.customQuestions.map((q, index) => (
              <div key={index} className="custom-question-item">
                <div className="question-text">
                  <strong>Q{index + 1}:</strong> {q.question}
                  {q.required && <span className="required-badge">Required</span>}
                </div>
                <button
                  type="button"
                  onClick={() => removeCustomQuestion(index)}
                  className="remove-question-btn"
                >
                  Remove
                </button>
              </div>
            ))}

            {formData.customQuestions.length < 3 && (
              <div className="add-question-form">
                <input
                  type="text"
                  value={customQuestion.question}
                  onChange={(e) => setCustomQuestion(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="Enter custom question..."
                  maxLength="200"
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={customQuestion.required}
                    onChange={(e) => setCustomQuestion(prev => ({ ...prev, required: e.target.checked }))}
                  />
                  <span>Required</span>
                </label>
                <button type="button" onClick={addCustomQuestion} className="add-question-btn">
                  Add Question
                </button>
              </div>
            )}
          </div>

          {existingForm && (
            <div className="form-stats">
              <h3>Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Requests:</span>
                  <span className="stat-value">{existingForm.totalRequests}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Pending:</span>
                  <span className="stat-value">{existingForm.pendingRequests}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Approved:</span>
                  <span className="stat-value">{existingForm.approvedRequests}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Rejected:</span>
                  <span className="stat-value">{existingForm.rejectedRequests}</span>
                </div>
              </div>
            </div>
          )}

          <div className="config-actions">
            <button type="submit" disabled={loading} className="save-config-btn">
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>
            {existingForm && (
              <button type="button" onClick={handleDelete} className="delete-config-btn">
                Delete Form
              </button>
            )}
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KeyRequestFormConfig;
